import React from "react"

import {DataSourceApi} from "../services/DataSourceApi"

import DataSource from "@customTypes/DataSource"
import DataSourceSync from "@customTypes/DataSourceSync"

import {MessageInterface} from "../components/common/Message";
import {DataSourceSyncApi} from "../services/DataSourceSyncApi";


export type DataSourceContextType = {
    addItem:        (dataSource: DataSource) => Promise<DataSource | void> | void
    deleteItem:     (dataSource: DataSource) => Promise<any> | void
    editItem:       (dataSource: DataSource) => Promise<DataSource | void> | void
    toggleItem:     (dataSource: DataSource, values: object) => Promise<DataSource | void> | void
    toggleDataSourceSyncIsEnabled: (item: DataSourceSync, values: object) => Promise<DataSourceSync | void> | void
    runDataSourceSync: (item: DataSourceSync) => Promise<void> | void
    fetchItem:      (id: number, include?: string) => void;
    fetchItems:     (search?: string, force?: boolean) => void
    items:          DataSource[] | undefined;
    loadingItems:   boolean;

    inProgress:     boolean;

    message: MessageInterface | undefined
    closeMessage: () => void
}

const initialState = {
    addItem:      () => {},
    deleteItem:   () => {},
    editItem:     () => {},
    fetchItem:    () => {},
    fetchItems:   () => {},
    items:        undefined,
    loadingItems: true,

    toggleItem:   () => {},
    toggleDataSourceSyncIsEnabled: () => {},
    runDataSourceSync: () => {},

    inProgress:   false,

    message:      undefined,
    closeMessage: () => {},
}

export const DataSourceContext = React.createContext<DataSourceContextType>(initialState);

interface DataSourceProviderProps {
    children: React.ReactNode
}

const DataSourceProvider: React.FC<DataSourceProviderProps> = ({ children }) => {

    const [items, setItems] = React.useState<DataSource[] | undefined>(initialState.items)
    const [loadingItems, setLoadingItems] = React.useState<boolean>(initialState.loadingItems)
    const [inProgress, setInProgress] = React.useState<boolean>(initialState.inProgress)
    const [message, setMessage] = React.useState<MessageInterface | undefined>(initialState.message)

    const fetchItem = async (id: number, include?: string): Promise<DataSource | void> => {
        setLoadingItems(true)

        const result = await DataSourceApi.get(id, include)
            .catch((e) => setErrorMessage(e))

        setLoadingItems(false)

        return result
    }

    const fetchItems = async (search?: string, force: boolean = false) => {
        if (items === undefined || force) {
            setLoadingItems(true)

            const result = await DataSourceApi.getAll(search)
                .catch((e) => setErrorMessage(e))

            if (result) {
                setItems(result)
                setLoadingItems(false)
            }
        }
    }

    const addItem = async (newDataSource: DataSource): Promise<DataSource | void> => {
        closeMessage()
        setInProgress(true)

        const result = await DataSourceApi.add(newDataSource, () => {})
            .catch(e => setErrorMessage(e))

        if (items && result) {
            setInProgress(false)
            setItems([result, ...items])
        }

        return result
    }

    const editItem = async (item: DataSource): Promise<DataSource | void> => {
        closeMessage()
        setInProgress(true)

        const result = await DataSourceApi.edit(item)
            .catch((e) => setErrorMessage(e))

        if (items && result) {
            const newItems: DataSource[] = items.map(oldItem => {
                return (oldItem.id === item.id && result.id) ? {...result} : oldItem
            })

            setInProgress(false)
            setItems(newItems)
        }

        return result
    }

    const toggleItem = async (item: DataSource, values: object): Promise<any> => {
        closeMessage()
        setInProgress(true)

        const result = await DataSourceApi.toggle(item, values)
            .catch((e) => setErrorMessage(e))

        if (items && result) {
            const newItems: DataSource[] = items.map(oldItem => {
                return (oldItem.id === item.id && result.id) ? {...result} : oldItem
            })

            setInProgress(false)
            setItems(newItems)
        }

        return result
    }

    const toggleDataSourceSyncIsEnabled = async (
        item: DataSourceSync, values: object
    ): Promise<DataSourceSync | void> => {
        closeMessage()
        setInProgress(true)

        const result = await DataSourceSyncApi.toggle(item, values)
            .catch((e) => setErrorMessage(e))

        if (result) {
            setInProgress(false)

            window.location.reload()
        }

        return result
    }

    const runDataSourceSync = async (item: DataSourceSync): Promise<void> => {
        closeMessage()
        setInProgress(true)

        const result = await DataSourceSyncApi.run(item)
            .catch((e) => setErrorMessage(e))

        if (result) {
            setInProgress(false)

            window.location.reload()
        }

        return result
    }

    const deleteItem = async (item: DataSource): Promise<any> => {
        closeMessage()
        setInProgress(true)

        const result = await DataSourceApi.delete(item.id)
            .catch((e) => setErrorMessage(e))

        if (items && result !== undefined) {
            const newItems = [...items]
            newItems.splice(newItems.findIndex(i => i.id === item.id), 1)

            setInProgress(false)
            setItems(newItems)
        }
    }

    const closeMessage = () => setMessage(undefined)

    const setErrorMessage = (e: any) => {
        setMessage({
            text: e.response.data.message || '?',
            type: 'error'
        })

        setInProgress(false)
        setLoadingItems(false)
    }

    return (
        <DataSourceContext.Provider
            value={{

                addItem,
                deleteItem,
                editItem,
                fetchItem,
                fetchItems,
                toggleItem,
                toggleDataSourceSyncIsEnabled,
                runDataSourceSync,

                items,
                loadingItems,
                inProgress,

                message,
                closeMessage,

            }}
        >
            {children}
        </DataSourceContext.Provider>
    );
}

export const useDataSources = () => {
    return React.useContext(DataSourceContext) as DataSourceContextType
}

export default DataSourceProvider